import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Link,
  Chip,
  Avatar,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const MODULE_COLORS = {
  keyword_analyzer: 'primary',
  episode_optimizer_v2: 'secondary',
  health_check_v2: 'success',
  contact_owner: 'info',
  social_audit: 'warning',
  cadence_v2: 'default',
  theme_map_v2: 'default',
  op3_downloads: 'info',
  sponsor_suggestions: 'success',
  category_themes: 'primary',
  guest_extractor: 'secondary',
  social_scan_v2: 'warning',
  contact_research: 'info',
  sponsor_finder: 'success',
};

const ChartsAnalyticsBI = () => {
  // Cached Shows state
  const [showsData, setShowsData] = useState([]);
  const [showsLoading, setShowsLoading] = useState(false);
  const [showsError, setShowsError] = useState('');
  const [showsFilter, setShowsFilter] = useState('');
  const [showsFilterInput, setShowsFilterInput] = useState('');
  const [showsPage, setShowsPage] = useState(0);
  const [showsRowsPerPage, setShowsRowsPerPage] = useState(25);

  // Analyze13 Usage State
  const [a13Days, setA13Days] = useState(30);
  const [a13Data, setA13Data] = useState(null);
  const [a13Loading, setA13Loading] = useState(false);
  const [a13Error, setA13Error] = useState('');

  const getJson = async (path) => {
    const r = await fetch(`/api/top-podcasts${path}`);
    const json = await r.json();
    if (!json.ok) throw new Error(json.error || 'Request failed');
    return json;
  };

  const loadCachedShows = async (q = '') => {
    setShowsLoading(true);
    setShowsError('');
    setShowsPage(0);
    try {
      const qs = q ? `?q=${encodeURIComponent(q)}` : '';
      const json = await getJson(`/bi/cached-shows${qs}`);
      setShowsData(json.shows || []);
    } catch (e) {
      setShowsError(e.message);
    } finally {
      setShowsLoading(false);
    }
  };

  const loadA13Events = async () => {
    setA13Loading(true);
    setA13Error('');
    try {
      const json = await getJson(`/bi/analyze13/events?days=${a13Days}`);
      setA13Data(json);
    } catch (e) {
      setA13Error(e.message);
    } finally {
      setA13Loading(false);
    }
  };

  useEffect(() => {
    loadCachedShows();
    loadA13Events();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowsSearch = (e) => {
    e.preventDefault();
    setShowsFilter(showsFilterInput);
    loadCachedShows(showsFilterInput);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ color: '#1976d2' }}>
          Charts Analytics BI
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Internal dashboard — cached shows, module usage, and sponsor analytics
        </Typography>
      </Box>

      {/* ── Cached Shows ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                Cached Podcasts
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {showsData.length} show{showsData.length !== 1 ? 's' : ''} in DB
                {showsFilter ? ` matching "${showsFilter}"` : ''}
              </Typography>
            </Box>
            <Box component="form" onSubmit={handleShowsSearch} sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search by title…"
                value={showsFilterInput}
                onChange={(e) => setShowsFilterInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="outlined" type="submit" disabled={showsLoading}>
                {showsLoading ? <CircularProgress size={18} /> : 'Search'}
              </Button>
              {showsFilter && (
                <Button variant="text" onClick={() => { setShowsFilterInput(''); setShowsFilter(''); loadCachedShows(''); }}>
                  Clear
                </Button>
              )}
            </Box>
          </Box>

          {showsError && <Alert severity="error" sx={{ mb: 2 }}>{showsError}</Alert>}

          {showsLoading && !showsData.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ width: 44 }} />
                    <TableCell><strong>Podcast</strong></TableCell>
                    <TableCell><strong>Author</strong></TableCell>
                    <TableCell><strong>Cached Modules</strong></TableCell>
                    <TableCell><strong>Last Updated</strong></TableCell>
                    <TableCell><strong>RSS</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {showsData.slice(showsPage * showsRowsPerPage, showsPage * showsRowsPerPage + showsRowsPerPage).map((show) => (
                    <TableRow key={show.showId} hover sx={{ verticalAlign: 'top' }}>
                      <TableCell sx={{ pt: 1.5 }}>
                        {show.image
                          ? <Avatar src={show.image} alt="" sx={{ width: 32, height: 32, borderRadius: 1 }} />
                          : <Avatar sx={{ width: 32, height: 32, borderRadius: 1, fontSize: 14 }}>{(show.title || '?')[0]}</Avatar>
                        }
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, fontWeight: 500 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {show.title || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160, color: 'text.secondary', fontSize: 13 }}>
                        {show.author || '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 380 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {show.cachedModules.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">none</Typography>
                          ) : show.cachedModules.map((m) => (
                            <Chip
                              key={m.moduleKey}
                              label={m.moduleKey}
                              size="small"
                              color={MODULE_COLORS[m.moduleKey] || 'default'}
                              variant="outlined"
                              title={`Updated: ${new Date(m.updatedAt).toLocaleDateString()}`}
                              sx={{ fontSize: 11, height: 20 }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                        {new Date(show.updatedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {show.rssUrl
                          ? <Link href={show.rssUrl} target="_blank" rel="noopener" sx={{ fontSize: 12 }}>RSS</Link>
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!showsLoading && showsData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No cached shows found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={showsData.length}
                page={showsPage}
                onPageChange={(_, newPage) => setShowsPage(newPage)}
                rowsPerPage={showsRowsPerPage}
                onRowsPerPageChange={(e) => { setShowsRowsPerPage(parseInt(e.target.value, 10)); setShowsPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Analyze13 Usage ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Analyze Usage
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Podcast selections and module runs
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Days</InputLabel>
                <Select value={a13Days} label="Days" onChange={(e) => setA13Days(e.target.value)}>
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                  <MenuItem value={365}>Last 365 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="contained" onClick={loadA13Events} disabled={a13Loading} fullWidth sx={{ height: 40 }}>
                {a13Loading ? <CircularProgress size={24} /> : 'Load Events'}
              </Button>
            </Grid>
          </Grid>

          {a13Error && <Alert severity="error" sx={{ mb: 2 }}>{a13Error}</Alert>}

          {a13Data && (() => {
            // Group rows by email+module, count runs, keep latest date
            const grouped = [];
            const seen = new Map();
            for (const row of (a13Data.rows || [])) {
              const key = `${row.email || ''}|${row.moduleId}`;
              if (seen.has(key)) {
                const entry = seen.get(key);
                entry.runs += 1;
                if (new Date(row.createdAt) > new Date(entry.lastRun)) entry.lastRun = row.createdAt;
              } else {
                const entry = { key, email: row.email, showTitle: row.showTitle, moduleId: row.moduleId, runs: 1, lastRun: row.createdAt };
                seen.set(key, entry);
                grouped.push(entry);
              }
            }

            return (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item>
                    <Paper variant="outlined" sx={{ px: 3, py: 1.5, textAlign: 'center' }}>
                      <Typography variant="h4">{a13Data.total ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary">Modules Run</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom><strong>Top Modules Run</strong></Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>Module</strong></TableCell>
                            <TableCell align="right"><strong>Runs</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(a13Data.topModules || []).map((row, i) => (
                            <TableRow key={i} hover>
                              <TableCell>{row._id}</TableCell>
                              <TableCell align="right">{row.count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" sx={{ mb: 1 }}><strong>Recent Events</strong></Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>Last Run</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Show</strong></TableCell>
                        <TableCell><strong>Module</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grouped.map((row) => (
                        <TableRow key={row.key} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{new Date(row.lastRun).toLocaleString()}</TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: row.email ? 'text.primary' : 'text.disabled' }}>
                            {row.email || 'anonymous'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
                            {row.showTitle || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {row.moduleId || '—'}
                              {row.runs > 1 && (
                                <Chip label={`×${row.runs}`} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            );
          })()}
        </CardContent>
      </Card>

    </Container>
  );
};

export default ChartsAnalyticsBI;
